/**
 * Signal notifications: browser (system) notifications plus an optional short chime.
 * Preferences are per browser (localStorage) and every access is guarded — storage can be unavailable.
 * Notifications only work while a MagicTradie! tab is open (it may be in the background).
 */
import type { SignalAlert, SignalOutcomeEvent } from '@contracts/signals';

const KEY = 'magictradie.signalAlerts';

export interface AlertPrefs {
  enabled: boolean;
  sound: boolean;
}

const DEFAULT_PREFS: AlertPrefs = { enabled: true, sound: true };

export function loadPrefs(): AlertPrefs {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw) as Partial<AlertPrefs>;
    return { enabled: p.enabled ?? true, sound: p.sound ?? true };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function savePrefs(prefs: AlertPrefs): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Preferences simply do not persist (private window, blocked storage).
  }
}

export type Permission = NotificationPermission | 'unsupported';

export function permission(): Permission {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

export async function requestPermission(): Promise<Permission> {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export function signalPrice(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });
}

export function alertTitle(a: SignalAlert): string {
  return `${a.direction} XAUUSD — ${a.strategyLabel}`;
}

export function alertBody(a: SignalAlert): string {
  return (
    `Entry ${signalPrice(a.entry)} · SL ${signalPrice(a.stopLoss)} · ` +
    `TP1 ${signalPrice(a.tp1)} · TP2 ${signalPrice(a.tp2)}` +
    (a.tp3 != null ? ` · TP3 ${signalPrice(a.tp3)}` : ` · net R ${a.netRR2.toFixed(2)}`)
  );
}

const OUTCOME_TEXT: Record<SignalOutcomeEvent['event'], string> = {
  tp1: 'reached TP1',
  tp2: 'reached TP2',
  tp3: 'reached TP3',
  sl: 'hit the stop loss',
  expired: 'expired without TP2 or SL',
  reversed: 'closed by the opposite signal',
};

export function outcomeTitle(e: SignalOutcomeEvent): string {
  return `${e.alert.direction} ${OUTCOME_TEXT[e.event]} (${signalPrice(e.price)})`;
}

function chime(urgent: boolean): void {
  try {
    const Ctx =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = urgent ? [880, 1175] : [660];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.18 + 0.16);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.18);
      osc.stop(ctx.currentTime + i * 0.18 + 0.17);
    });
    window.setTimeout(() => void ctx.close(), 800);
  } catch {
    // Audio blocked until the user interacts with the page; the visual alert still shows.
  }
}

function show(title: string, body: string, tag: string): void {
  if (permission() !== 'granted') return;
  try {
    const n = new Notification(title, { body, tag });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Some browsers only allow notifications from a service worker; the in-app alert still shows.
  }
}

export function notifyAlert(a: SignalAlert, prefs: AlertPrefs = loadPrefs()): void {
  if (!prefs.enabled) return;
  show(alertTitle(a), alertBody(a), a.id);
  if (prefs.sound) chime(true);
}

export function notifyOutcome(e: SignalOutcomeEvent, prefs: AlertPrefs = loadPrefs()): void {
  if (!prefs.enabled) return;
  show(outcomeTitle(e), e.alert.strategyLabel, `${e.alert.id}:${e.event}`);
  if (prefs.sound) chime(false);
}
